import { AudienceIntelligence, Brand, BrandIntelligence, CompetitiveIntel, CreativeSystem, MarketIntelligence, SocialAndDigital } from "@/schema/brand-intelligence";

const brand: Brand = {
  name: "Fora Financial LLC",
  industry: ["Specialized Finance", "Financial Services"],
  sub_industry: ["Alternative Small Business Lending", "Business Loans and Merchant Cash Advances"],
  founded_year: 2008,
  founded_original_name: "Paramount Merchant Funding",
  headquarters: ["New York, USA", "Miami, USA"],
  tagline_primary: "Not found",
  mission: "Fora Financial helps small businesses grow with fast, flexible funding through a simple, seamless process.",
  vision: '"To be the leading small business financing partner, helping entrepreneurs access capital quickly and transparently."',
  overview:
    "Fora Financial is a technology-enabled small business lender providing fast, flexible working-capital solutions across the U.S. It's known for quick decisions (as little as 4 hours) and funding as soon as 24 hours after acceptance.",
  scale: {
    revenue: 0,
    employees_estimate: 200,
    markets_served_count: 1,
    operating_countries_count: 1,
  },
  global_presence: {
    regions: [
      {
        region: "North America (United States)",
        headquarters: "New York, USA",
        active_markets: ["United States (49 states)"],
        market_type: "Direct lending",
      },
      {
        region: "North America (United States - Secondary Office)",
        headquarters: "Miami, USA",
        active_markets: ["United States"],
        market_type: "Direct lending",
      },
    ],
    key_markets_by_revenue_share: [
      {
        region: "United States",
        share_percent: 100,
        notes:
          "100% of company operations (exclusive U.S. market focus). 30 percent increase in Q1 2024 over Q1 2023 in overall funding volume, making this the strongest quarter in the firm's nearly 16-year history.",
      },
    ],
  },
};

const creativeSystem: CreativeSystem = {
  visual_identity: {
    colors: {
      primary: [
        { name: "Fora Green", hex: "#00A651" },
        { name: "Fora Blue", hex: "#0066B3" },
      ],
      secondary: [],
      accent: [],
    },
    typography: [
      { font_family: "Sans-Serif", weight: "Regular", usage: "Body text, navigation" },
      { font_family: "Sans-Serif", weight: "Bold or Semi-Bold", usage: "Headlines, CTAs" },
    ],
    logos: [{ variant: "Primary", url: "https://assets.zocket.com/uploads/545f02d5-65af-40a0-899c-50aa3cd9891e-Fora_Logo.jpg" }],
  },
  verbal_identity: {
    voice_traits: ["Supportive", "Consultative", "Transparent", "Empowering", "Professional yet Accessible", "Community-Focused", "Knowledgeable"],
    taglines: ["We Know Small Business", "Fast, flexible financing", "Personalized consultative support"],
    messaging_dos: [],
    messaging_donts: [],
    banned_words: [],
  },
  creative_assets: [
    "https://assets.zocket.com/uploads/7f7a0b5a-d192-4780-83b8-f38646b26dc2-IMG_4216.jpg",
    "https://assets.zocket.com/uploads/c9c19027-2838-44f5-a8cf-a876e712215c-IMG_4217.jpg",
    "https://assets.zocket.com/uploads/d99a365a-72c5-4fe5-bca5-b608f97d4e67-IMG_4218.jpg",
  ],
  compliance: {
    brand_safety_guidelines: [],
    copyright_trademark_usage: [],
  },
  brand_architecture: {
    available_brands: ["Fora Financial LLC"],
    selected_brand: "Fora Financial",
  },
  ai_creative_memory: {
    design_memory: {
      pattern_accuracy: "94%",
      patterns_learned: 342,
      avg_generation: "1.5s",
    },
    style_drift_alerts: [
      {
        title: "Q4 Email Campaign",
        description: "Button CTA color deviates from Fora blue #0066B3",
        severity: "high",
      },
      {
        title: "Small Business Loan Landing Page",
        description: "Heading font size inconsistent with typography scale",
        severity: "medium",
      },
      {
        title: "Social Media Ad - LinkedIn",
        description: "Logo spacing does not meet minimum clearance requirements",
        severity: "low",
      },
      {
        title: "Invoice Factoring Brochure",
        description: "Fora Green #00A651 usage exceeds recommended 20% threshold",
        severity: "medium",
      },
    ],
    preference_learning: [
      { preference: "Professional Trust", score: 93, change: 5 },
      { preference: "Blue Accents", score: 89, change: 4 },
      { preference: "Business Focus", score: 86, change: 3 },
      { preference: "Clear CTAs", score: 82, change: 6 },
    ],
  },
};

const marketIntelligence: MarketIntelligence = {
  kpis: {
    sentiment: 7.8,
    sentiment_graph: [
      { metric: "Customer Satisfaction Score", value: "4.4/5", delta: "+0.2" },
      { metric: "Funding Speed", value: "24-48 hrs", delta: "Improved" },
      { metric: "Trustpilot Rating", value: "4.4/5", delta: "Stable" },
      { metric: "Customer Base", value: "55,000+", delta: "+15%" },
      { metric: "Approval Speed", value: "4 hours", delta: "-20% faster" },
      { metric: "BBB Rating", value: "A+", delta: "Maintained" },
      { metric: "Share of Voice (Alternative Lending)", value: "~3-5%", delta: "+0.5%" },
      { metric: "Market Presence (Funding Volume)", value: "$4B+", delta: "+30% YoY" },
    ],
  },
  sentiment_summary: {
    time_range: ["Last 12 months (2024-2025)"],
    themes: {
      positive: [
        "Exceptional Customer Service & Representative Quality",
        "Fast Funding & Approval Process",
        "Accessibility for Bad Credit Businesses",
        "Straightforward Application Process",
        "Professional & Knowledgeable Staff",
        "Flexible Financing Options",
        "No Collateral Required",
      ],
      neutral: ["Factor Rate Pricing Structure", "High Revenue Requirements", "Short Repayment Terms", "Third-Party Referrals", "Limited Product Range"],
      negative: [
        "High Interest Rates/Costs",
        "Daily/Weekly Payment Frequency",
        "Unsolicited Marketing Communications",
        "Lack of Follow-Up",
        "Payment Confusion/Disputes",
        "Reduced Loan Amounts Post-Approval",
        "Limited Transparency on Rates",
      ],
    },
  },
  distribution: [
    { brand: "OnDeck", share: 9 },
    { brand: "Kabbage (American Express)", share: 8 },
    { brand: "Funding Circle", share: 7 },
    { brand: "Bluevine", share: 6 },
    { brand: "Others", share: 70 },
  ],
  sentiment_by_product: [
    { product: "Short-Term Business Loans", sentiment: 7.9, change_delta: 5 },
    { product: "Revenue Advances (MCA)", sentiment: 7.2, change_delta: 2 },
    { product: "Lines of Credit (via partners)", sentiment: 7.5, change_delta: 0 },
    { product: "SBA Loans (via partners)", sentiment: 7.8, change_delta: 3 },
    { product: "Invoice Factoring (via partners)", sentiment: 7.4, change_delta: 1 },
    { product: "Equipment Financing (via partners)", sentiment: 7.6, change_delta: 2 },
  ],
  top_topics: {
    praise: [
      { topic: "Outstanding Customer Service & Representative Quality", weight: 10 },
      { topic: "Fast Approval & Funding Speed", weight: 9 },
      { topic: "Accessibility for Bad Credit/Startups", weight: 8 },
      { topic: "Simple Application Process", weight: 7 },
      { topic: "Professional & Knowledgeable Staff", weight: 7 },
      { topic: "No Collateral Required", weight: 6 },
      { topic: "Flexible Financing Options", weight: 6 },
    ],
    complaints: [
      { topic: "High Interest Rates & Costs", weight: 10 },
      { topic: "Daily/Weekly Payment Burden", weight: 8 },
      { topic: "Short Repayment Terms (Max 24 months)", weight: 6 },
      { topic: "Unsolicited Marketing/Spam Calls", weight: 5 },
      { topic: "High Revenue Requirements ($240K annual)", weight: 5 },
      { topic: "Lack of Rate Transparency", weight: 4 },
      { topic: "Payment Confusion & Disputes", weight: 3 },
    ],
  },
};

const competitiveIntel: CompetitiveIntel = {
  primary_competitors: [
    { name: "OnDeck", threat_level: "high", market_share: 9, sentiment: 82 },
    { name: "Bluevine", threat_level: "high", market_share: 7, sentiment: 78 },
    { name: "Kabbage (now American Express Business Blueprint)", threat_level: "high", market_share: 8, sentiment: 80 },
    { name: "Fundbox", threat_level: "medium-high", market_share: 5, sentiment: 75 },
    { name: "Kapitus", threat_level: "medium", market_share: 4, sentiment: 70 },
    { name: "National Funding (National Business Capital)", threat_level: "medium", market_share: 6, sentiment: 85 },
    { name: "Credibly", threat_level: "medium", market_share: 3, sentiment: 72 },
    { name: "Swift Financial (Swift Capital)", threat_level: "low-medium", market_share: 2.5, sentiment: 68 },
  ],
  secondary_competitors: [
    { name: "PayPal Working Capital", threat_level: "medium", market_share: 0, sentiment: 0 },
    { name: "Square Capital", threat_level: "medium", market_share: 0, sentiment: 0 },
    { name: "Lendio", threat_level: "medium", market_share: 0, sentiment: 0 },
    { name: "Funding Circle", threat_level: "medium", market_share: 0, sentiment: 0 },
    { name: "Biz2Credit", threat_level: "medium", market_share: 0, sentiment: 0 },
    { name: "LendingClub Business", threat_level: "medium", market_share: 0, sentiment: 0 },
  ],
  share_of_voice: [
    {
      channel: "Digital/Search Marketing",
      distribution: {
        OnDeck: 18.1,
        "Kabbage/AmEx Blueprint": 15.2,
        Bluevine: 13.3,
        Fundbox: 10.5,
        "Fora Financial": 8.6,
        "National Funding": 8.6,
        "Others (Kapitus, Credibly, etc.)": 25.7,
      },
    },
    {
      channel: "Social Media",
      distribution: {
        "PayPal Working Capital": 23,
        "Square Capital": 17,
        OnDeck: 14,
        Bluevine: 11,
        "Kabbage/AmEx": 11,
        "Fora Financial": 4,
        Others: 20,
      },
    },
    {
      channel: "Industry Publications/PR",
      distribution: {
        OnDeck: 21,
        "Kabbage/AmEx Blueprint": 17,
        "Funding Circle": 14,
        Bluevine: 11,
        "Fora Financial": 7,
        Others: 30,
      },
    },
  ],
  competitive_benchmarking: [
    {
      metric_name: "Customer Satisfaction Score (Trustpilot Rating)",
      brands: [
        { brand_name: "Fora Financial", score: 90 },
        { brand_name: "OnDeck", score: 98 },
        { brand_name: "Bluevine", score: 88 },
        { brand_name: "Kabbage", score: 94 },
        { brand_name: "National Business Capital", score: 100 },
        { brand_name: "Fundbox", score: 85 },
      ],
    },
    {
      metric_name: "BBB Rating Score",
      brands: [
        { brand_name: "Fora Financial", score: 100 },
        { brand_name: "OnDeck", score: 100 },
        { brand_name: "Bluevine", score: 100 },
        { brand_name: "Kabbage", score: 85 },
        { brand_name: "National Funding", score: 90 },
        { brand_name: "Fundbox", score: 80 },
      ],
    },
    {
      metric_name: "Approval Rate Score",
      brands: [
        { brand_name: "Fora Financial", score: 75 },
        { brand_name: "OnDeck", score: 70 },
        { brand_name: "Bluevine", score: 72 },
        { brand_name: "Kabbage", score: 78 },
        { brand_name: "Fundbox", score: 80 },
        { brand_name: "National Business Capital", score: 90 },
      ],
    },
    {
      metric_name: "Funding Speed Score (24-hour capability)",
      brands: [
        { brand_name: "Fora Financial", score: 85 },
        { brand_name: "OnDeck", score: 95 },
        { brand_name: "Bluevine", score: 90 },
        { brand_name: "Fundbox", score: 88 },
        { brand_name: "Kabbage", score: 92 },
        { brand_name: "PayPal Working Capital", score: 98 },
      ],
    },
    {
      metric_name: "Product Range Diversity Score",
      brands: [
        { brand_name: "Fora Financial", score: 60 },
        { brand_name: "OnDeck", score: 80 },
        { brand_name: "Bluevine", score: 85 },
        { brand_name: "Fundbox", score: 70 },
        { brand_name: "National Business Capital", score: 90 },
        { brand_name: "Biz2Credit", score: 95 },
      ],
    },
    {
      metric_name: "Market Accessibility Score (Credit/Revenue Requirements)",
      brands: [
        { brand_name: "Fora Financial", score: 85 },
        { brand_name: "OnDeck", score: 70 },
        { brand_name: "Bluevine", score: 72 },
        { brand_name: "Fundbox", score: 88 },
        { brand_name: "Kabbage", score: 75 },
        { brand_name: "SBG Funding", score: 90 },
      ],
    },
  ],
  positioning: {
    competitive_advantages: ["Lenient credit requirements (570 minimum)", "Fast funding (24-72 hours)"],
    improvement_areas: ["Lower social media presence compared to top competitors"],
    disruption_watchlist: ["Embedded finance players (Stripe Capital, Parafin, Resolve)", "Platform-integrated lenders (PayPal, Square)"],
  },
};

const audienceIntelligence: AudienceIntelligence = {
  audience_summary:
    "Fora Financial's target audience consists of small to mid-sized business owners seeking alternative financing solutions, primarily in the United States. These business owners are typically aged 45-54, with a significant portion over 55. They are predominantly male, with a median income of $69,730 USD, and operate in urban and suburban areas. They are growth-oriented, risk-tolerant, and value quick access to capital.",
  core_demographics: {
    primary_age_range: "45-54 years",
    gender_split: { male: 60, female: 40 },
    income_median_usd: 69730,
    location_type: "Urban/Suburban",
    demographics_distribution: {
      age: [
        { age_range: "18-34", percentage: 18 },
        { age_range: "35-44", percentage: 24 },
        { age_range: "45-54", percentage: 32 },
        { age_range: "55-64", percentage: 18 },
        { age_range: "65+", percentage: 8 },
      ],
      education: [
        { education_level: "Bachelor's+", percentage: 42 },
        { education_level: "Some College", percentage: 30 },
        { education_level: "Graduate Degree", percentage: 12 },
        { education_level: "High School", percentage: 16 },
      ],
      geographic: [
        { location: "Suburban", percentage: 42 },
        { location: "Urban", percentage: 38 },
        { location: "Rural", percentage: 20 },
      ],
    },
  },
  occupation_distribution: [
    { occupation: "Small Business Owners", percentage: 45 },
    { occupation: "Retail & Hospitality Operators", percentage: 18 },
    { occupation: "Contractors & Trades", percentage: 14 },
    { occupation: "Professional Services", percentage: 12 },
    { occupation: "Healthcare Practice Owners", percentage: 11 },
  ],
  interests: {
    top_categories: [
      { category: "Business Growth & Scaling", score: 9 },
      { category: "Financial Management & Cash Flow", score: 9 },
      { category: "Industry-Specific Trade Associations", score: 8 },
      { category: "Small Business Technology & Automation", score: 8 },
      { category: "Entrepreneurship & Business Strategy", score: 7 },
      { category: "Networking & Business Communities", score: 7 },
      { category: "Tax Planning & Compliance", score: 7 },
      { category: "Marketing & Customer Acquisition", score: 6 },
      { category: "Work-Life Balance", score: 6 },
      { category: "Economic & Industry News", score: 6 },
    ],
    brand_affinities: [
      { brand: "QuickBooks/Intuit", affinity: 47.5 },
      { brand: "Square/PayPal", affinity: 42.5 },
      { brand: "Facebook/Meta Business Suite", affinity: 37.5 },
      { brand: "Google Workspace", affinity: 37.5 },
      { brand: "Amazon Business", affinity: 32.5 },
      { brand: "Microsoft 365", affinity: 32.5 },
      { brand: "Shopify", affinity: 22.5 },
      { brand: "LinkedIn", affinity: 22.5 },
      { brand: "Salesforce/HubSpot", affinity: 17.5 },
      { brand: "Gusto/ADP", affinity: 17.5 },
    ],
  },
  behavioral_insights: {
    shopping_behavior: {
      purchase_channel_distribution: {
        "Online application/Direct website": 52,
        "Phone consultation": 28,
        "Referral/Partner network": 12,
        "In-person/Branch visit": 8,
      },
      purchase_frequency: "1.3-1.8x per year",
      avg_order_value_usd: 105000,
    },
    decision_journey: {
      stages: [
        {
          stage: 1,
          name: "Cash Flow Crisis or Growth Opportunity",
          description: "Business owner identifies immediate capital need for operations, inventory, expansion, or emergency",
          duration_days: 2,
        },
        {
          stage: 2,
          name: "Research & Options Discovery",
          description: "Search for loan products, compare lenders, read reviews, check rates and terms online",
          duration_days: 5,
        },
        {
          stage: 3,
          name: "Lender Comparison & Vetting",
          description: "Compare product features, rates, approval requirements, funding speed across multiple lenders",
          duration_days: 3,
        },
        {
          stage: 4,
          name: "Submit Application & Documentation",
          description: "Complete online application, provide bank statements, speak with representative, receive approval decision",
          duration_days: 1,
        },
        {
          stage: 5,
          name: "Accept Terms & Receive Funds",
          description: "Review offer, accept terms, receive funding in as little as 24 hours, begin repayment",
          duration_days: 1,
        },
      ],
    },
    media_and_device_usage: {
      device_preference: { Mobile: 48, Desktop: 42, Tablet: 10 },
      media_channels: {
        Facebook: 28,
        LinkedIn: 22,
        "Google Search/SEM": 18,
        "Email Marketing": 12,
        Instagram: 10,
        "Industry Publications/Blogs": 6,
        YouTube: 4,
      },
    },
  },
  platforms: {
    distribution: {
      Facebook: 28,
      LinkedIn: 22,
      "Google Search/SEM": 18,
      "Email Marketing": 12,
      Instagram: 10,
      "Industry Publications/Blogs": 6,
      YouTube: 4,
    },
  },
};

const socialAndDigital: SocialAndDigital = {
  platform_metrics: [
    { platform: "LinkedIn", followers: 7679, health_score: 85 },
    { platform: "Twitter/X", followers: 3887, health_score: 70 },
    { platform: "Instagram", followers: 2951, health_score: 70 },
    { platform: "Facebook", followers: 2500, health_score: 70 },
    { platform: "YouTube", followers: 0, health_score: 20 },
    { platform: "TikTok", followers: 0, health_score: 0 },
    { platform: "Google+", followers: 0, health_score: 0 },
    { platform: "Trustpilot", followers: 976, health_score: 85 },
  ],
  sentiment_distribution: [
    { label: "Positive", percentage: 60 },
    { label: "Neutral", percentage: 30 },
    { label: "Negative", percentage: 10 },
  ],
  recent_comments: [
    {
      user: "JohnDoe123",
      platform: "LinkedIn",
      timeAgo: "2 days ago",
      status: "positive",
      commentText: "Great insights on small business financing!",
      postContext: "Educational Blog Content",
    },
    {
      user: "JaneSmith456",
      platform: "Twitter/X",
      timeAgo: "1 week ago",
      status: "neutral",
      commentText: "Interesting case study, but more details would be helpful.",
      postContext: "Customer Success Stories",
    },
  ],
  engagement: {
    engagement_by_platform: [
      { platform: "LinkedIn", engagement_rate: 2.2 },
      { platform: "Instagram", engagement_rate: 65 },
      { platform: "Twitter/X", engagement_rate: 15 },
      { platform: "Facebook", engagement_rate: 12.5 },
      { platform: "TikTok", engagement_rate: 0 },
      { platform: "YouTube", engagement_rate: 0 },
    ],
    best_performing_content_types: [
      { content_type: "Educational Blog Content", performance: 9 },
      { content_type: "Customer Success Stories", performance: 9 },
      { content_type: "Corporate News & Hiring Posts", performance: 7 },
      { content_type: "Economic Commentary", performance: 8 },
      { content_type: "Carousel Posts", performance: 9 },
      { content_type: "Photo/Image Posts", performance: 9 },
      { content_type: "Branded Hashtag Content", performance: 7 },
    ],
    follower_growth_trend: [
      { month: "January", followers: 12000 },
      { month: "July", followers: 21000 },
      { month: "August", followers: 14500 },
      { month: "September", followers: 14900 },
      { month: "October", followers: 14530 },
      { month: "November", followers: 12500 },
      { month: "December", followers: 15500 },
    ],
  },
};

export const mBrandIntelligenceForaFinancial: BrandIntelligence = {
  brand,
  creative_system: creativeSystem,
  market_intelligence: marketIntelligence,
  competitive_intel: competitiveIntel,
  audience_intelligence: audienceIntelligence,
  social_and_digital: socialAndDigital,
};
